import React, { useState, useEffect } from "react";
import { Trans, useTranslation } from "react-i18next";
import { AnimatePresence } from "framer-motion";
import { FaFacebookF } from "react-icons/fa";
import { RiInstagramFill } from "react-icons/ri";
import { IoArrowForward } from "react-icons/io5";
import { FaLinkedinIn } from "react-icons/fa";
import { motion } from "framer-motion";
import { debounce } from "lodash";
import { useNavigate, useLocation } from "react-router-dom";
import OrderModal from "../../components/payment/OrderModal";
import PartnersModal from "../partners/partnersModal/PartnersModal";
import { getServices } from "../../axios/api/getServicesApi";
import ScrollToTopButton from "../../components/home/ScrollToTopButton";
import { link } from "../../axios/axiosInstance";

const Footer = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const userData = JSON.parse(localStorage.getItem("userData"));
  const token = userData?.token;
  const [payment, setPayment] = useState(null);
  const [show, setShow] = useState(false);
  const [orderNumber, setOrderNumber] = useState(null);
  const [showButton, setShowButton] = useState(false);

  const [showModal, setShowModal] = useState(false);

  const openModal = () => {
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const activeRoute = location.pathname;
  const lang = i18n.language;

  const quickLinks = [
    { name: "home", route: `/${lang}/` },
    { name: "aboutUs", route: `/${lang}/about/` },
    { name: "partners", route: `/${lang}/partners/` },
    { name: "contactUS", route: `/${lang}/contact/` },
    { name: "privacyPolicy", route: `/${lang}/privacy-policy/` },
  ];

  const [services, setServices] = useState([]);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const handleResize = debounce(() => {
    setWindowWidth(window.innerWidth);
  }, 100);

  const handleScroll = debounce(() => {
    const yOffset = window.pageYOffset;
    setShowButton(yOffset > 500);
  }, 100);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    window.addEventListener("resize", handleResize);

    return () => {
      // Clean up event listeners and debounced functions
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleResize);
      handleScroll.cancel();
      handleResize.cancel();
    };
  }, [handleResize, handleScroll]);

  const getServiceUrl = (id, partnerCount) => {
    return partnerCount == 0
      ? `/${lang}/coming-soon/`
      : `/${lang}/partners/partner-list/${id}`;
  };

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await getServices();
        setServices(response?.services || []);
      } catch (error) {
        console.error("Failed to fetch services:", error);
      }
    };

    fetchServices();
  }, []);

  useEffect(() => {
    if (!token) return;
    const events = new EventSource(
      `${link}/api/v1/events/payments?token=${token}`,
      {
        withCredentials: true,
      },
    );
    events.onmessage = (event) => {
      const data = JSON.parse(event.data);
      if (data.partner_status === "finished") {
        setPayment(data);
        setOrderNumber(data.order_number);
        setShow(true);
      }
    };

    events.onerror = (err) => {
      console.error("SSE error:", err);
      events.close();
    };

    return () => events.close();
  }, [token]);

  const handleClose = () => {
    setShow(false);

    fetch(`${link}/api/v1/closed_rate_popup`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ orderNumber: orderNumber }),
    });
  };

  return (
    <div className="relative left-0 right-0 w-full min-h-[600px] overflow-hidden flex items-center justify-center p-[1.5em] md:p-[5em] ">
      {/* 🔹 Background Spline Scene */}
      <div className="absolute inset-0 z-0">
        {/* <iframe
          src="https://my.spline.design/footeranimationabstract-FnC6B3szROjXBmuXoJgO3Z5c/"
          frameborder="0"
          width="100%"
          height="100%"
        ></iframe> */}
        <video
          src="/assets/footer-animation.mp4"
          autoPlay
          loop
          muted
          preload="auto"
          playsInline
          className="w-full h-full object-cover"
        />
      </div>

      {/* 🔹 Glass Overlay Section */}
      <div className="relative z-10 flex flex-col justify-center items-center h-full px-6 md:px-14 py-20 bg-black/40 backdrop-blur-xl rounded-[30px] shadow-2xl border border-white/20 max-w-[1400px] w-full">
        {/* Inner Content Wrapper */}
        <div className="w-full flex flex-col md:flex-row justify-end items-start gap-20 text-white">
          {/* 🔸 Column 1 — Left */}
          <div className="flex flex-col gap-6 w-[100%] md:w-[80%] mr-[0em] md:mr-[10em] [@media(min-width:1200px)_and_(max-width:1400px)]:mr-[1em] [@media(min-width:1200px)_and_(max-width:1368px)]:w-[60%]">
            <h2 className="text-[40px] md:text-[60px] font-[500] text-white mb-4 mt-[-20px]">
              {t("letsTalk")}
            </h2>

            {/* Email and Phone */}
            <div className="flex flex-col gap-2 text-primary text-[25px]">
              <a href="mailto:info@myservice.am">info@myservice.am</a>
              <a href="tel:+37433338600">+374 (33) 338-600</a>
            </div>

            {/* Google Map Embed */}
            <div className="w-[100%] h-[200px] rounded-[20px] overflow-hidden shadow-lg border border-white/10">
              <iframe
                title="myservice location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1194.5227997104175!2d44.49622959854496!3d40.18196492553699!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x406abd00e88ee08f%3A0x1b6d2cc3a716a0ee!2zNDAsIDEgRHpvcmFwaSBTdCwgWWVyZXZhbiwg0JDRgNC80LXQvdC40Y8!5e0!3m2!1sru!2sfr!4v1769612311002!5m2!1sru!2sfr"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              ></iframe>
            </div>

            {/* Address */}
            <p className="text-white/80 text-[20px]">
              {t("addressFooter") || "Armenia, Yerevan, Gyurjian 58, 45"}
            </p>

            {/* Social Icons */}
            <div className="flex gap-4 mt-2">
              <a
                href="https://www.facebook.com/profile.php?id=61581794224621"
                target="_blank"
                rel="noopener noreferrer"
                className="w-12 h-12 bg-primary rounded-full flex items-center justify-center hover:scale-105 transition-transform"
                aria-label="Visit our Facebook page"
              >
                <FaFacebookF className="text-white text-xl w-7 h-7" />
              </a>
              <a
                href="https://www.instagram.com/myservice.am"
                target="_blank"
                rel="noopener noreferrer"
                className="w-12 h-12 bg-primary rounded-full flex items-center justify-center hover:scale-105 transition-transform"
                aria-label="Visit our Instagram page"
              >
                <RiInstagramFill className="text-white text-2xl w-7 h-7" />
              </a>
              <a
                href="https://www.linkedin.com/company/myservice-am/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-12 h-12 bg-primary rounded-full flex items-center justify-center hover:scale-105 transition-transform"
                aria-label="Visit our LinkedIn page"
              >
                <FaLinkedinIn className="text-white text-2xl w-7 h-7" />
              </a>
            </div>
          </div>

          {/* 🔸 Column 2 — Right with 2 Subcolumns + Payments */}
          <div className="w-[100%] md:w-[80%] flex flex-col gap-10 ml-0 md:ml-[6em]">
            {/* Subcolumns */}
            <div className="grid grid-cols-2 gap-10">
              {/* Subcolumn 1 — Quick Links */}
              <div>
                <h3 className="text-[18px] md:text-[25px] mb-3">{t("subColumn1Title")}</h3>
                <ul className="flex flex-col gap-2 text-[16px] md:text-[18px] opacity-90">
                  {quickLinks.map((item) => ( 
                    <li key={item.route}>
                      <a
                        href={item.route}
                        rel="noopener noreferrer"
                        className="block w-full"
                      >
                        <motion.button
                          initial="rest"
                          whileHover="hover"
                          animate="rest"
                          transition={{
                            type: "spring",
                            stiffness: 300,
                            damping: 15,
                          }}
                          className={`relative flex items-center gap-3 transition-colors duration-300 group w-full text-left ${
                            activeRoute === item.route
                              ? "text-primary"
                              : "hover:text-primary"
                          }`}
                        >
                          {t(item.name)}
                          <motion.span
                            variants={{
                              rest: {
                                opacity: 0,
                                rotate: -45,
                                x: -5,
                                y: 5,
                                scale: 0.8,
                              },
                              hover: {
                                opacity: 1,
                                rotate: -45,
                                x: 0,
                                y: 0,
                                scale: 1.5,
                              },
                            }}
                            transition={{ duration: 0.3, ease: "easeOut" }}
                            className="inline-block text-primary text-[20px]"
                          >
                            <IoArrowForward />
                          </motion.span>
                        </motion.button>
                      </a>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Subcolumn 2 — Services */}
              <div>
                <h3 className="text-[18px] md:text-[25px] mb-3">{t("subColumn2Title")}</h3>
                <ul className="flex flex-col gap-2 text-[16px] md:text-[18px] opacity-90">
                  {services.map((item) => (
                    <li key={item.id}>
                      <a
                        href={getServiceUrl(item.id, item.partnerCount)}
                        rel="noopener noreferrer"
                        className="block w-full"
                      >
                        <motion.button
                          initial="rest"
                          whileHover="hover"
                          animate="rest"
                          transition={{
                            type: "spring",
                            stiffness: 300,
                            damping: 15,
                          }}
                          className={`relative flex items-center gap-3 transition-colors duration-300 capitalize group w-full text-left ${
                            activeRoute === item.id
                              ? "text-primary"
                              : "hover:text-primary"
                          }`}
                        >
                          {t(item.name)}
                          <motion.span
                            variants={{
                              rest: {
                                opacity: 0,
                                rotate: -45,
                                x: -5,
                                y: 5,
                                scale: 0.8,
                              },
                              hover: {
                                opacity: 1,
                                rotate: -45,
                                x: 0,
                                y: 0,
                                scale: 1.5,
                              },
                            }}
                            transition={{ duration: 0.3, ease: "easeOut" }}
                            className="inline-block text-primary text-[20px]"
                          >
                            <IoArrowForward />
                          </motion.span>
                        </motion.button>
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <AnimatePresence>
              {showButton && <ScrollToTopButton onClick={scrollToTop} />}
            </AnimatePresence>

            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 items-center gap-6">
              {/* 💳 Online Payments Section */}
              <div>
                <h3 className="text-[18px] md:text-[25px] mb-4">
                  <Trans
                    i18nKey="onlinePayments"
                    components={{ break: <br /> }} 
                  />
                </h3>
                <div className="flex items-center gap-3">
                  <img
                    src="/assets/visa-icon.png"
                    alt="Visa"
                    className="h-5 md:h-4 w-auto object-contain"
                  />
                  <img
                    src="/assets/mastercard-icon.png"
                    alt="Mastercard"
                    className="h-6 md:h-5 w-auto object-contain"
                  />
                  <img
                    src="/assets/arca-icon.png"
                    alt="Arca"
                    className="h-5 md:h-4 w-auto object-contain"
                  />
                  <img
                    src="/assets/conversebank-icon.png"
                    alt="Converse Bank"
                    className="h-8 md:h-7 w-auto object-contain"
                  />
                </div>
              </div>

              <div className="flex">
                <button
                  className="flex items-center justify-center gap-3 bg-primary text-white px-[20px] py-2 rounded-[50px] transition-all duration-300 hover:bg-white hover:text-black font-[500] text-[18px]"
                  onClick={openModal}
                >
                  {t("becomeAPartner1")}
                </button>
              </div>
            </div>
          </div>
        </div>

        <OrderModal
          isOpen={show}
          onClose={handleClose}
          orderNumber={orderNumber}
          payment={payment}
        />

        {showModal && (
          <PartnersModal showModal={showModal} onClose={handleCloseModal} />
        )}

        {/* 🔹 Bottom Bar */}
        <div className="w-full mt-12 border-t border-white/30 pt-4 text-center text-white/70">
          © {new Date().getFullYear()} myservice.am — {t("allRightsReserved")}
        </div>
      </div>
    </div>
  );
};

export default Footer;
